import React, { createContext, useContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from 'hooks/useUser';
import { useAdminContext } from '../../../../../context/AdminContext';
import { fetchEmployees } from '../services/employeeService';
import { fetchRoleDefinitions } from '../services/roleDefinitionService';
import { fetchUsers } from '../services/userService';

const PeopleAdminContext = createContext();

/**
 * Hook to access PeopleAdmin context data and functions
 * @returns {Object} Context value containing employees, users, roles, and utility functions
 * @throws {Error} If used outside of PeopleAdminProvider
 */
export const usePeopleAdmin = () => {
  const context = useContext(PeopleAdminContext);
  if (!context) {
    throw new Error('usePeopleAdmin must be used within a PeopleAdminProvider');
  }
  return context;
};

/**
 * Provider component for PeopleAdmin context
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {React.Component} Context provider wrapper
 */
export function PeopleAdminProvider({ children }) {
  const { user } = useUser();
  const { selectedClient } = useAdminContext();
  const queryClient = useQueryClient();
  const clientId = selectedClient;

  // State for managing UI state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedRoleDefinition, setSelectedRoleDefinition] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch employees
  const {
    data: employees = [],
    isLoading: isLoadingEmployees,
    error: employeesError,
    refetch: refetchEmployees
  } = useQuery({
    queryKey: ['employees', clientId],
    queryFn: () => fetchEmployees(clientId),
    enabled: !!clientId,
  });

  // Fetch role definitions
  const {
    data: roleDefinitions = [],
    isLoading: isLoadingRoles,
    error: rolesError,
    refetch: refetchRoles
  } = useQuery({
    queryKey: ['roleDefinitions', clientId],
    queryFn: () => fetchRoleDefinitions(clientId),
    enabled: !!clientId,
    staleTime: 0, // Consider data stale immediately
    cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Fetch users
  const {
    data: users = [],
    isLoading: isLoadingUsers,
    error: usersError,
    refetch: refetchUsers
  } = useQuery({
    queryKey: ['users', clientId],
    queryFn: () => fetchUsers(clientId),
    enabled: !!clientId,
  });

  // Combined loading state
  const isLoading = isLoadingEmployees || isLoadingRoles || isLoadingUsers;
  const isRefetching = false; // Will be implemented with refetch logic

  // Combined error state
  const error = employeesError || rolesError || usersError;

  // Refetch all data
  const refetch = async () => {
    await Promise.all([
      refetchEmployees(),
      refetchRoles(),
      refetchUsers()
    ]);
  };

  // Calculate metrics
  const metrics = useMemo(() => {
    const activeEmployees = employees.filter(e => e.isActive !== false);
    const inactiveEmployees = employees.filter(e => e.isActive === false);
    const activeRoles = roleDefinitions.filter(r => r.isActive !== false);
    const usersWithEmployee = users.filter(u => u.employeeId);
    const employeesWithUser = employees.filter(e => e.userId);

    return {
      totalEmployees: employees.length,
      activeEmployees: activeEmployees.length,
      inactiveEmployees: inactiveEmployees.length,
      totalRoleDefinitions: roleDefinitions.length,
      activeRoleDefinitions: activeRoles.length,
      totalUsers: users.length,
      usersWithEmployee: usersWithEmployee.length,
      employeesWithUser: employeesWithUser.length,
      systemAccessCoverage: employees.length > 0 
        ? Math.round((employeesWithUser.length / employees.length) * 100)
        : 0,
    };
  }, [employees, roleDefinitions, users]);

  // Utility functions for finding relationships
  const getEmployeeForUser = (userId) => {
    if (!userId) return null;
    const user = users.find(u => u.id === userId);
    if (!user?.employeeId) return null;
    return employees.find(e => e.id === user.employeeId);
  };

  const getUserForEmployee = (employeeId) => {
    if (!employeeId) return null;
    const employee = employees.find(e => e.id === employeeId);
    if (!employee?.userId) return null;
    return users.find(u => u.id === employee.userId);
  };

  // Invalidate queries function for updates
  const invalidateQueries = async (queryKeys = []) => {
    if (queryKeys.length === 0) {
      // Invalidate all people admin queries
      await queryClient.invalidateQueries({ queryKey: ['employees'] });
      await queryClient.invalidateQueries({ queryKey: ['roleDefinitions'] });
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    } else {
      // Invalidate specific queries
      for (const key of queryKeys) {
        await queryClient.invalidateQueries({ queryKey: [key] });
      }
    }
  };

  const value = {
    // Data
    employees,
    roleDefinitions,
    users,
    metrics,
    
    // Loading states
    isLoading,
    isRefetching,
    error,
    
    // Selected items
    selectedEmployee,
    setSelectedEmployee,
    selectedRoleDefinition,
    setSelectedRoleDefinition,
    selectedUser,
    setSelectedUser,
    
    // Utility functions
    getEmployeeForUser,
    getUserForEmployee,
    
    // Actions
    refetch,
    invalidateQueries,
    
    // Client info
    clientId
  };

  return (
    <PeopleAdminContext.Provider value={value}>
      {children}
    </PeopleAdminContext.Provider>
  );
}

PeopleAdminProvider.propTypes = {
  children: PropTypes.node.isRequired,
};